(function () {
  'use strict';

  // ---------------------------------------------------------------
  // Config
  // ---------------------------------------------------------------

  const SELECTORS = {
    list: '.product-offer-list',
    offer: '.offer',
    productForm: 'form[action*="/cart/add"]',
    tierSelectorWrap: '.offer--tier-selector',
    gridHorizontal: '.offer__grid--horizontal',
    priceDisplay: '.modal_price .price .money[data-price]',
    compareAtPriceDisplay: '.modal_price .compare-at-price[data-compare-at-price]'
  };

  // ---------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------

  function formatMoney(cents) {
    // Use Shopify's formatMoney if available (respects store currency format)
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const moneyFormat = (window.theme && window.theme.moneyFormat) || 'CHF {{amount}}';
      return Shopify.formatMoney(cents, moneyFormat);
    }
    // Fallback: simple CHF formatting
    return 'CHF ' + (cents / 100).toFixed(2);
  }

  // ---------------------------------------------------------------
  // Tier selector: interactive qty selection that syncs with main form
  // ---------------------------------------------------------------

  function initTierSelector() {
    const wrappers = document.querySelectorAll(SELECTORS.tierSelectorWrap);
    if (wrappers.length === 0) return;

    wrappers.forEach(wrapper => {
      const buttons = wrapper.querySelectorAll('[data-tier-qty]');
      const fill = wrapper.querySelector('[data-tier-fill]');
      const markers = Array.from(wrapper.querySelectorAll('[data-tier-marker]'));

      const productForm = document.querySelector(SELECTORS.productForm);
      if (!productForm) return;

      // Ensure a quantity input exists in the form. Flex's default form
      // doesn't include one when qty is disabled in theme settings,
      // so we inject a hidden input that the tier selector controls.
      let qtyInput = productForm.querySelector('input[name="quantity"]');
      if (!qtyInput) {
        qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = 'quantity';
        qtyInput.value = '1';
        productForm.appendChild(qtyInput);
      }

      // Read product price from the wrapper (in cents)
      const basePrice = parseInt(wrapper.dataset.productPrice, 10) || 0;

      if (!buttons.length || !markers.length) return;

      const tierQtys = markers.map(m => parseInt(m.dataset.tierMarker, 10) || 1);
      const tierCount = tierQtys.length;

      function updatePriceDisplay(qty, discountPercent) {
        // Query the price display fresh each call so we always write to the
        // current DOM element, not a stale cached reference (the theme's
        // price-ui library may replace/re-render the element on init).
        const priceDisplay = document.querySelector(SELECTORS.priceDisplay);
        if (!priceDisplay || !basePrice) return;

        // Calculate discounted total: base × qty × (100 - discount) / 100
        const totalCents = Math.round(basePrice * qty * (100 - discountPercent) / 100);
        priceDisplay.textContent = formatMoney(totalCents);

        // Show compare-at total (full price × qty) when discount is active
        const compareDisplay = document.querySelector(SELECTORS.compareAtPriceDisplay);
        if (compareDisplay) {
          if (discountPercent > 0) {
            const fullTotal = basePrice * qty;
            compareDisplay.textContent = formatMoney(fullTotal);
            compareDisplay.style.display = '';
          } else {
            compareDisplay.textContent = '';
          }
        }
      }

      function selectTier(btn) {
        const qty = parseInt(btn.dataset.tierQty, 10) || 1;
        const discount = parseInt(btn.dataset.tierDiscount, 10) || 0;

        // Visual: update button selected state
        buttons.forEach(b => b.classList.remove('offer__tier-selector-btn--selected'));
        btn.classList.add('offer__tier-selector-btn--selected');

        // Calculate fill width based on tier INDEX, not absolute qty.
        // This ensures the fill lands exactly on the corresponding marker
        // regardless of whether tier qtys are 1,2,3,5 or 1,3,7,12.
        const tierIndex = tierQtys.indexOf(qty);
        if (fill && tierCount > 1 && tierIndex !== -1) {
          const percent = (tierIndex / (tierCount - 1)) * 100;
          fill.style.width = percent + '%';
        }

        // Update markers: any marker with qty <= selected qty is "reached"
        markers.forEach(marker => {
          const markerQty = parseInt(marker.dataset.tierMarker, 10) || 1;
          if (markerQty <= qty) {
            marker.classList.add('offer__tier-selector-marker--reached');
          } else {
            marker.classList.remove('offer__tier-selector-marker--reached');
          }
        });

        // Sync with form quantity input
        qtyInput.value = qty;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));

        // Update price display to reflect total based on qty and discount
        updatePriceDisplay(qty, discount);

        // Analytics event
        wrapper.dispatchEvent(new CustomEvent('offer:tier-selected', {
          bubbles: true,
          detail: {
            offerId: wrapper.dataset.offerId,
            qty,
            discount,
            placement: wrapper.dataset.placement
          }
        }));
      }

      buttons.forEach(btn => {
        btn.addEventListener('click', () => selectTier(btn));
      });

      // Initialize: select the first button by default
      const initialBtn = wrapper.querySelector('.offer__tier-selector-btn--selected') || buttons[0];
      if (initialBtn) selectTier(initialBtn);
    });
  }

  // ---------------------------------------------------------------
  // Analytics bridge: forward custom events to dataLayer
  // ---------------------------------------------------------------

  function initAnalyticsBridge() {
    if (typeof window === 'undefined') return;

    document.addEventListener('offer:tier-selected', (e) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'offer_tier_selected',
        offer_id: e.detail.offerId,
        qty: e.detail.qty,
        discount: e.detail.discount,
        placement: e.detail.placement
      });
    });

    if ('IntersectionObserver' in window) {
      const seen = new WeakSet();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !seen.has(entry.target)) {
            seen.add(entry.target);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'offer_impression',
              offer_id: entry.target.dataset.offerId,
              placement: entry.target.dataset.placement
            });
          }
        });
      }, { threshold: 0.5 });

      document.querySelectorAll(SELECTORS.offer).forEach(el => observer.observe(el));
    }
  }

  // ---------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------

  function init() {
    initTierSelector();
    initAnalyticsBridge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();